<script>
import LandingMiniBanner from '@rei/landing-mini-banner';
import SimpleTopCategories from '@rei/simple-top-categories';
import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import BrandRail from '@rei/brand-rail';
import LandingFeaturedTiles from '@rei/landing-featured-tiles';
import { StoriesComponent } from '@rei/landing-story';
import SeoBlock from '@rei/landing-seo-block';
import convertToHtml from '@rei/rich-text-renderer';
import { useDynamicContent } from '@rei/cs-preview-fetch';
import { CdrGrid, CdrSkeleton, CdrSkeletonBone } from '@rei/cedar';

import SecondaryHeading from '../../common/components/secondary-heading/SecondaryHeading.vue';
import AdditionalSavings from '../../common/components/additional-savings/AdditionalSavings.vue';
import CategoryNav from '../../common/components/category-nav/CategoryNav.vue';
import experimentation from '../../common/mixins/experimentation';
import AsyncProductRecommendations
  from '../../common/async-components/product-recommendations/AsyncProductRecommendations.vue';
import analytics from '../../common/mixins/analytics';
import getRecs from '../../utils/recommendations';

import {
  configDestructuring,
  formatFrequentlyAsked,
  formatImageComponent,
  formatMiniBannerComponent,
  transformLandingFeaturedTilesComponent,
  transformLinkCollectionToCategories,
  transformLinksToAdditionalSavings,
  transformPromotionToLandingStories,
} from '../../utils/formatContentStackData';

export default {
  name: 'OffPrice',
  components: {
    SimpleTopCategories,
    BrandRail,
    SecondaryHeading,
    LandingFeaturedTiles,
    FrequentlyAskedQuestions,
    AdditionalSavings,
    LandingMiniBanner,
    AsyncProductRecommendations,
    CategoryNav,
    StoriesComponent,
    SeoBlock,
    CdrSkeleton,
    CdrSkeletonBone,
    CdrGrid,
  },
  mixins: [analytics, experimentation],
  props: {
    additionalData: { type: Object, required: true, default: () => {} },
    componentModels: { type: Object, required: true, default: () => {} },
    richRelevanceEnabled: { type: Boolean, default: true },
    testData: { type: Object, required: false, default: () => {} },
  },
  setup(props) {
    const getPreviewUrl = () => `/home/rs/off-price/live-preview?entryId=${props.componentModels?.uid}`;
    const { content: reactiveComponentModels } = useDynamicContent(props, getPreviewUrl);
    if (reactiveComponentModels.value?.componentModels) {
      reactiveComponentModels.value = reactiveComponentModels.value.componentModels;
    }
    // add topCategoriesTest to content
    reactiveComponentModels.value.topCategoriesTest = {};
    const HEADING_TEXT = 'Deals you might like';

    return {
      reactiveComponentModels,
      HEADING_TEXT,
    };
  },
  data() {
    return {
      recsPlacement: [],
      recsProducts: [],
      adobeTargetOfferEnabled: true,
      isOutlet: false,
      isDeals: false,
      recsLoading: true,
    };
  },
  computed: {
    renderingMap() {
      return {
        lead: !!this.lead,
        'product-recommendations': !!(this.richRelevanceEnabled && !!this.recsPlacement[0]),
        'top-categories': !!this.topCategories,
        'featured-categories': !!this.featuredCategories,
        'featured-brands': !!this.featuredBrands,
        'featured-stories': !!this.featuredStories,
        'single-story': !!this.singleStory,
        'seo-block': !!this.seoBlock,
        'frequently-asked-questions': !!this.faqContent,
        'category-navigation': !!this.categoryNav,
        'additional-savings': !!this.additionalSavings,
      };
    },
    uid() {
      const { uid } = this.reactiveComponentModels || {};
      return uid;
    },
    lead() {
      const { lead } = this.reactiveComponentModels || {};
      const { heading, description } = lead || {};
      const headingOpts = { level: 'h1', size: 'lg-standard' };
      const defaults = { padding: 'tight', palette: { surface: 'secondary' } };
      const copy = { heading, description };
      const links = lead?.link ? [lead?.link] : null;
      const miniBannerProps = formatMiniBannerComponent(
        {
          ...lead,
          copy,
          links,
          palette: 'primary',
        },
        defaults,
        headingOpts
      );
      return (lead && { miniBannerProps }) || null;
    },
    featuredCategories() {
      const { featuredCategories } = this.reactiveComponentModels || {};
      const {
        id, heading, subHeading: body, categories, cta,
      } = featuredCategories || {};
      const ctaMap = { text: cta?.title, target: cta?.href, palette: configDestructuring(featuredCategories, 'palette') };

      const transformedCategories = transformLinkCollectionToCategories({ links: categories }, 'categories');
      const renderComponent = !!featuredCategories?.categories?.length
          && !!transformedCategories?.length;
      return (renderComponent && {
        id,
        heading,
        body,
        categories: transformedCategories,
        cta: ctaMap,
        grid: '5',
        hasBackground: true,
      }) || null;
    },
    topCategories() {
      const { topCategories } = this.reactiveComponentModels || {};
      const config = { isMediaFullWidth: false };
      const categoryTiles = transformLandingFeaturedTilesComponent(topCategories, config);
      const renderComponent = !!topCategories?.heading && !!categoryTiles?.featuredTiles?.length;
      return (renderComponent && {
        heading: topCategories?.heading,
        props: {
          ...categoryTiles,
        },
      }) || null;
    },
    featuredBrands() {
      const { featuredBrands } = this.reactiveComponentModels || {};
      if (featuredBrands) {
        const formattedBrands = transformLinkCollectionToCategories(featuredBrands, 'off-price-brands');
        return {
          id: featuredBrands?.id || 'featured-brands',
          heading: featuredBrands?.heading,
          props: {
            categories: formattedBrands,
            categoriesConfig: { hasBackground: true },
            mediaConfig: {
              hasBackground: false,
              padding: {
                mobile: '0', desktop: '0', hasBackground: false,
              },

            },
          },
        };
      }
      return null;
    },
    singleStory() {
      const { singleStory } = this.reactiveComponentModels || {};
      const palette = {
        surface: 'secondary',
        content: 'brand',
      };
      if (singleStory) {
        const landingStoryOptions = {
          grid: 'single',
          palette,
          aspectRatio: '4/3',
        };
        const contentBlockOptions = { palette };
        const headingOptions = { level: 'h2', size: 'md-standard' };
        const actionsOptions = {
          useCustomText: true,
        };

        const formattedStories = transformPromotionToLandingStories(
          [{ ...singleStory?.promotion, actionsOptions }],
          landingStoryOptions,
          '',
          {},
          contentBlockOptions,
          headingOptions,
        );
        return {
          ...formattedStories,
        };
      }
      return null;
    },
    featuredStories() {
      const { featuredStories = {} } = this.reactiveComponentModels || {};
      const { promotionSections = [] } = featuredStories || {};
      const mapPromotions = promotionSections.map((promotionSection) => ({
        ...promotionSection.promotion,
        config: {
          ...promotionSection?.config,
          palette: configDestructuring(promotionSection, 'theme'),
        },
        actionsOptions: {
          allSecondaryButtons: configDestructuring(promotionSection, 'secondaryButtonsOnly'),
        },
      }));
      const headingOptions = { level: 'h2', size: 'sm-standard' };
      const landingStoryOptions = {
        grid: 'double',
        aspectRatio: '5/4',
      };
      const formattedStories = transformPromotionToLandingStories(
        mapPromotions,
        landingStoryOptions,
        null,
        null,
        null,
        headingOptions,
      );
      const renderComponent = !!mapPromotions?.length;
      return (renderComponent && { ...formattedStories }) || null;
    },
    seoBlock() {
      const { seoBlock } = this.reactiveComponentModels || {};
      if (seoBlock) {
        const { image, heading, body } = seoBlock || {};
        const id = seoBlock?.id;
        const media = formatImageComponent(image);
        const formattedHeading = heading ? `<h2>${heading}</h2>` : '';
        const text = formattedHeading + convertToHtml(body);

        return (
          (seoBlock && {
            id: id || 'seo-block',
            props: {
              media,
              markup: {
                text,
              },
            },
          })
          || null
        );
      }
      return null;
    },
    faqContent() {
      const { frequentlyAsked } = this.reactiveComponentModels || {};
      const faq = formatFrequentlyAsked(frequentlyAsked);
      const renderComponent = !!faq?.questionsAndAnswers?.length;
      return (renderComponent && {
        faq,
      }) || null;
    },
    categoryNav() {
      const { 'category-navigation': categoryNav } = this.additionalData || {};
      const category = categoryNav?.children;
      const categoryText = categoryNav?.text;

      const mapped = (category && Object.values(category).map((subcategory) => {
        const { children, text, href } = subcategory;
        const mappedChildren = Object.values(children)
          .map((child) => ({ text: child.text, href: child.href }));
        return {
          children: mappedChildren,
          text,
          href,
        };
      })) || [];

      const renderComponent = !!mapped.length;
      return (renderComponent && {
        id: categoryNav?.id || 'category-navigation',
        props: {
          linkCategories: mapped,
          categoryText,
        },
      }) || null;
    },
    additionalSavings() {
      const { additionalSavings } = this.reactiveComponentModels || {};
      return transformLinksToAdditionalSavings(additionalSavings);
    },
  },
  async mounted() {
    if (this.adobeTargetOfferEnabled && !this.isLivePreview) {
      await this.initExperimentation({
        clientSideEnabled: this.adobeTargetOfferEnabled,
      });
    }

    this.handleRecommendations();
  },
  methods: {
    buildGetRecsArgs(placementName) {
      return [
        [{ placementType: placementName }],
        null,
        null,
        'landing-page',
      ];
    },
    async handleRecommendations() {
      this.isOutlet = this.uid === 'bltf222639f603993c7';
      this.isDeals = this.uid === 'blt70d20e3013985c4e';
      if (this.richRelevanceEnabled) {
        this.recsLoading = true;
        this.recsPlacement = await getRecs(...this.buildGetRecsArgs('home_page.GarageHome1'));
        try {
          if (this.isOutlet) {
            this.recsProducts = await getRecs(...this.buildGetRecsArgs('home_page.LP_outlet_rec_1'));
          }
          if (this.isDeals) {
            this.recsProducts = await getRecs(...this.buildGetRecsArgs('home_page.LP_deals_rec_1'));
          }
        } catch (error) {
          this.recsLoading = false;
          console.error('Recs error', error);
        } finally {
          this.recsLoading = false;
        }
      }
    },
  },
};
</script>

<template>
  <div
    data-ui="off-price"
    class="off-price"
  >
    <!-- Lead -->
    <section
      v-if="renderingMap['lead']"
      :data-location-name="analyticsMap['lead']"
      data-ui="off-price-lead"
      class="off-price__container off-price__container--no-padding"
    >
      <landing-mini-banner v-bind="lead.miniBannerProps" />
    </section>

    <CdrSkeleton
      v-if="recsLoading && !recsProducts[0]"
      class="off-price__container off-price__container--narrow"
    >
      <section>
        <div class="inset">
          <CdrSkeletonBone type="heading" />
          <div class="skeleton-card--body">
            <cdr-grid
              class="skeleton-card__grid"
              gutter="medium"
            >
              <CdrSkeletonBone type="square" />
              <CdrSkeletonBone type="square" />
              <CdrSkeletonBone type="square" />
              <CdrSkeletonBone type="square" />
              <CdrSkeletonBone type="square" />
            </cdr-grid>
          </div>
        </div>
      </section>
    </CdrSkeleton>

    <section
      v-if="renderingMap['product-recommendations']"
      id="product-recommendations"
      class="off-price__container off-price__container--narrow"
      data-ui="product-recommendations"
      :data-location-name="analyticsMap['product-recommendations']"
    >
      <async-product-recommendations
        :placement="recsProducts[0]"
        :metrics="metrics"
        use-custom-heading
      >
        <template #customHeading>
          <secondary-heading
            :text="HEADING_TEXT"
            data-ui="off-price-product-recommendations--heading"
          />
        </template>
      </async-product-recommendations>
    </section>

    <!-- top-categories -->
    <section
      v-if="renderingMap['top-categories']"
      :data-location-name="analyticsMap['top-categories']"
      data-ui="off-price-top-categories"
      class="off-price__container off-price__container--narrow"
    >
      <secondary-heading
        v-if="topCategories.heading"
        :text="topCategories.heading"
        data-ui="off-price-top-categories--heading"
      />
      <landing-featured-tiles v-bind="topCategories.props" />
    </section>

    <!-- featured-categories -->
    <section
      v-if="renderingMap['featured-categories']"
      :data-location-name="analyticsMap['featured-categories']"
      data-ui="off-price-featured-categories"
      class="off-price__container off-price__container--narrow"
    >
      <secondary-heading
        v-if="featuredCategories.heading"
        :text="featuredCategories.heading"
        data-ui="off-price-featured-categories-heading"
      />
      <simple-top-categories v-bind="featuredCategories" />
    </section>

    <!-- featured-brands -->
    <section
      v-if="renderingMap['featured-brands']"
      :data-location-name="analyticsMap['featured-brands']"
      data-ui="off-price-featured-brands"
      class="off-price__container off-price__container--narrow"
    >
      <secondary-heading
        v-if="featuredBrands.heading"
        :text="featuredBrands.heading"
        data-ui="off-price-featured-brands--heading"
      />
      <brand-rail v-bind="featuredBrands.props" />
    </section>

    <!-- featured-stories -->
    <section
      v-if="renderingMap['featured-stories']"
      :data-location-name="analyticsMap['featured-stories']"
      data-ui="off-price-featured-stories"
      class="off-price__container off-price__container--narrow"
    >
      <stories-component
        v-bind="featuredStories"
      />
    </section>

    <!-- single-story -->
    <section
      v-if="renderingMap['single-story']"
      :data-location-name="analyticsMap['single-story']"
      data-ui="off-price-single-story"
      class="off-price__container off-price__container--narrow"
    >
      <stories-component
        class="off-price__single-story"
        v-bind="singleStory"
      />
    </section>

    <!-- seo-block -->
    <section
      v-if="renderingMap['seo-block']"
      :data-analyics-id="analyticsMap['seo-block']"
      data-ui="off-price-seo-block"
      class="off-price off-price__container--narrow"
    >
      <seo-block
        class="off-price__seo-block"
        v-bind="seoBlock.props"
      />
    </section>

    <!-- faq -->
    <frequently-asked-questions
      v-if="renderingMap['frequently-asked-questions']"
      data-ui="off-price-frequently-asked-questions"
      :data-location-name="analyticsMap['frequently-asked-questions']"
      class="off-price__container off-price-container--no-padding"
      v-bind="faqContent"
    />

    <!-- Category Navigation -->
    <section
      v-if="renderingMap['category-navigation']"
      :id="categoryNav.id"
      :data-location-name="analyticsMap['category-navigation']"
      data-ui="category-nav"
      class="off-price__container off-price__category-nav category"
    >
      <div class="off-price__container--narrow">
        <category-nav v-bind="categoryNav.props" />
      </div>
    </section>

    <!-- additional-savings -->
    <section
      v-if="renderingMap['additional-savings']"
      :data-location-name="analyticsMap['additional-savings']"
      data-ui="off-price-additional-savings"
      class="off-price__container off-price__container--narrow"
    >
      <additional-savings v-bind="additionalSavings" />
    </section>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.off-price {
  // Temporary fix till we figure out mini banner lockup bug.
  .mini-banner {
    .ls-content-block__lockup img { width: 409px; }
  }

  &__container {
    padding-top: $cdr-space-one-and-a-half-x;
    padding-bottom: $cdr-space-one-and-a-half-x;

    .secondary-heading {
      @include cdr-text-heading-serif-600;
    }

    @include cdr-sm-mq-up {
      padding-top: $cdr-space-two-x;
      padding-bottom: $cdr-space-two-x;
    }

    @include cdr-md-mq-up{
      .secondary-heading{
        @include cdr-text-heading-serif-800;
      }
    }

    &--narrow {
      @include cdr-container;
    }

    &--no-padding {
      padding: 0;
    }
  }

  &__category-nav {
    background-color: $cdr-color-background-secondary;
  }

  &__recommendations {
    overflow: hidden;
  }

  &__seo-block {
    .landing-seo-block__image img{
      border-radius: 0.8rem;
    }
  }
  .skeleton-card {
    &--body {
      margin-top: $cdr-space-one-and-a-half-x;
      min-width: 990px;
      overflow: hidden;
    }
    &__grid {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    }
  }
}
</style>
